/*
 * pdp-founder.js
 * ---------------------------------------------------------------
 * Counts the proof figures up when they scroll into view.
 *
 * Same idea as the counter in the reviews section, generalised: that
 * one assumed a whole number and a suffix, so "4.9" would have counted
 * to 4 and "Australia" would have rendered NaN. This reads the figure
 * apart into whatever sits before the digits, the digits themselves and
 * whatever follows -- so 4.9, 1,700+, 90% and a word all behave.
 *
 * Anyone who has asked for reduced motion sees the final figure
 * immediately. A number sliding up the screen is decoration, and
 * decoration is exactly what that setting is asking us to skip.
 *
 * Vanilla ES6, no dependencies.
 * ---------------------------------------------------------------
 */

(function () {
  'use strict';

  var DURATION = 1400;
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function parse(text) {
    var m = String(text).match(/^([^\d]*)([\d.,]+)(.*)$/);
    if (!m) return null;

    var digits = m[2];
    var grouped = digits.indexOf(',') !== -1;
    var decimals = 0;
    var dot = digits.lastIndexOf('.');
    if (dot !== -1 && digits.length - dot - 1 <= 2) decimals = digits.length - dot - 1;

    var value = parseFloat(digits.replace(/,/g, ''));
    if (isNaN(value)) return null;

    return { before: m[1], after: m[3], value: value, decimals: decimals, grouped: grouped };
  }

  function format(n, spec) {
    var out = spec.decimals ? n.toFixed(spec.decimals) : String(Math.floor(n));
    if (spec.grouped) {
      var parts = out.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      out = parts.join('.');
    }
    return spec.before + out + spec.after;
  }

  function run(el) {
    if (el.dataset.counted === 'true') return;
    el.dataset.counted = 'true';

    var spec = parse(el.textContent.trim());
    /* No digits in it -- "Australia" and the like are left alone. */
    if (!spec || REDUCED) return;

    var start = null;
    var final = el.textContent.trim();

    function step(now) {
      if (start === null) start = now;
      var p = Math.min(1, (now - start) / DURATION);
      /* Ease out, so it settles rather than stopping dead. */
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = p >= 1 ? final : format(spec.value * eased, spec);
      if (p < 1) window.requestAnimationFrame(step);
    }

    el.textContent = format(0, spec);
    window.requestAnimationFrame(step);
  }

  function init(scope) {
    var nodes = (scope || document).querySelectorAll('[data-fdr-count]');
    if (!nodes.length) return;

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(function (el) { el.dataset.counted = 'true'; });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          run(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    nodes.forEach(function (el) { io.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
